// src/slices/recipientSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/payments/recipients/`;

export const fetchRecipients = createAsyncThunk(
  'recipients/fetch',
  async (filters = {}, thunkAPI) => {
    try {
      const { page, pageSize, ...rest } = filters;
      const params = new URLSearchParams();

      Object.entries(rest).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      if (page !== undefined) {
        // Opt-in server pagination: DRF PageNumberPagination is 1-indexed.
        params.append('page', page + 1);
        params.append('page_size', pageSize || 25);
      } else {
        // Callers that don't pass `page` keep getting the full unpaginated
        // list, matching this thunk's behavior before pagination was added.
        params.append('page_size', 'all');
      }

      const response = await axios.get(`${BASE_URL}?${params.toString()}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Fetch failed');
    }
  }
);

// Fetches every row matching the given filters in one unpaginated call - used
// by Export/Print, which need the full filtered result set, not one page.
export const fetchRecipientsForExport = createAsyncThunk(
  'recipients/fetchForExport',
  async (filters = {}, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });
      params.append('page_size', 'all');

      const response = await axios.get(`${BASE_URL}?${params.toString()}`);
      const data = response.data;
      return Array.isArray(data) ? data : data.results;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Export fetch failed');
    }
  }
);

// Server-computed { count, total_amount } for the current filters, so summary
// cards stay accurate without fetching every row just to sum them client-side.
export const fetchRecipientsSummary = createAsyncThunk(
  'recipients/fetchSummary',
  async (filters = {}, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${BASE_URL}summary/?${params.toString()}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Summary fetch failed');
    }
  }
);


export const deleteRecipient = createAsyncThunk('recipients/delete', async (id, thunkAPI) => {
  try {
    await axios.delete(`${BASE_URL}${id}/`);
    return id;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data || 'Delete failed');
  }
});

export const fetchRecipientsByStudent = createAsyncThunk(
  'recipients/fetchByStudent',
  async (studentId, thunkAPI) => {
    try {
      // page_size=all: one student's receipts are few, and callers expect a plain array
      const res = await axios.get(`${BASE_URL}?student=${studentId}&school_year=current&page_size=all`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || 'Fetch by student failed');
    }
  }
);

export const updateRecipient = createAsyncThunk(
  'recipients/update',
  async ({ id, ...data }, thunkAPI) => {
    try {
      const formData = new FormData();
      
      // Basic recipient data
      if (data.student_id) formData.append('student', data.student_id);
      if (data.school_fee) formData.append('school_fee', data.school_fee);
      if (data.payment_type) formData.append('payment_type', data.payment_type);
      formData.append('amount', data.amount);
      formData.append('date', data.date);
      formData.append('received', data.received);

      // ✅ FIX: Properly handle cheque data with all fields in updates
      if (data.bankNumber) formData.append('bankNumber', data.bankNumber);
      if (data.branchNumber) formData.append('branchNumber', data.branchNumber);
      if (data.bankName) formData.append('bankName', data.bankName);
      if (data.branchName) formData.append('branchName', data.branchName);
      if (data.countryCode) formData.append('countryCode', data.countryCode);
      if (data.accountNumber) formData.append('accountNumber', data.accountNumber);
      if (data.chequeNumber) formData.append('chequeNumber', data.chequeNumber);  // ✅ Added this
      if (data.chequeDueDate) formData.append('chequeDueDate', data.chequeDueDate);
      if (data.chequeImage) formData.append('chequeImage', data.chequeImage);
      if (data.description) formData.append('description', data.description);  // ✅ Added this
      if (data.visaReceiptNumber) formData.append('visaReceiptNumber', data.visaReceiptNumber);
      if (data.note) formData.append('note', data.note);


      console.log('🔍 Update FormData being sent:', Array.from(formData.entries())); // Debug log

      const response = await axios.put(`${BASE_URL}${id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Update failed');
    }
  }
);

export const fetchRecipientById = createAsyncThunk('recipients/fetchById', async (id, thunkAPI) => {
  try {
    const res = await axios.get(`${BASE_URL}${id}/`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data || 'فشل في التحميل');
  }
});

export const fetchNotReceivedRecipients = createAsyncThunk(
  'recipients/fetchNotReceived',
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(`${BASE_URL}not_received/`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || 'Fetch not-received failed');
    }
  }
);



export const createRecipient = createAsyncThunk('finance/createRecipient', async (recipient) => {
    const formData = new FormData();
    
    // Basic recipient data
    if (recipient.student_id) formData.append('student', recipient.student_id);
    if (recipient.school_fee) formData.append('school_fee', recipient.school_fee);
    if (recipient.payment_type) formData.append('payment_type', recipient.payment_type);
    formData.append('amount', recipient.amount);
    formData.append('date', recipient.date);
    formData.append('school_year', recipient.school_year);
    formData.append('received', recipient.received);

    // ✅ FIX: Properly handle cheque data with all fields
    if (recipient.bankNumber) formData.append('bankNumber', recipient.bankNumber);
    if (recipient.branchNumber) formData.append('branchNumber', recipient.branchNumber);
    if (recipient.bankName) formData.append('bankName', recipient.bankName);
    if (recipient.branchName) formData.append('branchName', recipient.branchName);
    if (recipient.countryCode) formData.append('countryCode', recipient.countryCode);
    if (recipient.accountNumber) formData.append('accountNumber', recipient.accountNumber);
    if (recipient.chequeNumber) formData.append('chequeNumber', recipient.chequeNumber);  // ✅ Added this
    if (recipient.chequeDueDate) formData.append('chequeDueDate', recipient.chequeDueDate);
    if (recipient.chequeImage) formData.append('chequeImage', recipient.chequeImage);
    if (recipient.description) formData.append('description', recipient.description);  // ✅ Added this
    if (recipient.visaReceiptNumber) formData.append('visaReceiptNumber', recipient.visaReceiptNumber);
    if (recipient.note) formData.append('note', recipient.note);


    console.log('🔍 FormData being sent:', Array.from(formData.entries())); // Debug log

    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/payments/recipients/`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
  
    return response.data;
});
  

const recipientSlice = createSlice({
  name: 'recipients',
  initialState: {
    list: [],
    count: 0,
    filteredData: [],
    notReceived: [],
    data: [],
    summary: { count: 0, total_amount: 0 },
    summaryStatus: 'idle',
    status: 'idle',
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecipients.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchRecipients.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (Array.isArray(action.payload)) {
          state.list = action.payload;
          state.count = action.payload.length;
        } else {
          state.list = action.payload.results;
          state.count = action.payload.count;
        }
      })
      .addCase(fetchRecipientsSummary.pending, (state) => {
        state.summaryStatus = 'loading';
      })
      .addCase(fetchRecipientsSummary.fulfilled, (state, action) => {
        state.summaryStatus = 'succeeded';
        state.summary = action.payload;
      })
      .addCase(fetchRecipientsSummary.rejected, (state, action) => {
        state.summaryStatus = 'failed';
        state.error = action.payload;
      })
      .addCase(createRecipient.fulfilled, (state, action) => {
        state.list.push(action.payload);
      })
      .addCase(createRecipient.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchRecipients.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchRecipientById.pending, (state) => {
        state.selectedRecipientStatus = 'loading';
      })
      .addCase(fetchNotReceivedRecipients.pending, (state) => {
  state.status = 'loading';
})
.addCase(fetchNotReceivedRecipients.fulfilled, (state, action) => {
  state.status = 'succeeded';
  state.notReceived = action.payload;
})
.addCase(fetchNotReceivedRecipients.rejected, (state, action) => {
  state.status = 'failed';
  state.error = action.payload;
})
      .addCase(fetchRecipientById.fulfilled, (state, action) => {
        state.selectedRecipient = action.payload;
        state.selectedRecipientStatus = 'succeeded';
      })
      .addCase(fetchRecipientById.rejected, (state, action) => {
        state.selectedRecipientStatus = 'failed';
        state.selectedRecipientError = action.payload;
      })
      .addCase(deleteRecipient.fulfilled, (state, action) => {

        state.list = state.list.filter((r) => r.id !== action.payload);
      })
      .addCase(fetchRecipientsByStudent.fulfilled, (state, action) => {
  state.status = 'succeeded';
  state.list = action.payload;
})
.addCase(fetchRecipientsByStudent.rejected, (state, action) => {
  state.status = 'failed';
  state.error = action.payload;
})
      .addCase(updateRecipient.fulfilled, (state, action) => {
        const index = state.list.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(updateRecipient.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteRecipient.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export default recipientSlice.reducer;
